var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var _a;
import DataTable from '@/components/data-table/DataTable';
import SingleNoteDialog from '@/components/SingleNoteDialog/SingleNoteDialog.vue';
import { GoalPlannerNote } from '@/models/GoalPlannerNote';
import { Group } from '@/models/Group';
import { Log } from '@/models/Log';
import { User } from '@/models/User';
import { sanitizeString } from '@/support/String';
import { DateTime } from 'luxon';
import { Component, Prop, Vue } from 'vue-property-decorator';
import StudentSelect from '@/components/StudentSelect/StudentSelect.vue';
import { LabelKey } from '@/models/Label';
let GoalPlannerLogbookDialog = class GoalPlannerLogbookDialog extends Vue {
    constructor() {
        // #region @VModel
        // #endregion
        super(...arguments);
        // #endregion
        // #region @Refs
        // #endregion
        // #region Class properties
        this.subjectNoteDialogIsOpen = false;
        this.tableVisibility = {
            checkboxes: false,
            limit: false,
            search: false,
        };
        this.actionItems = [
            { id: 'model:user_subject', name: 'Status' },
            { id: 'model:subject_user_note', name: 'Notitie' },
        ];
        this.tableFilters = {
            action: undefined,
        };
        this.fromDate = DateTime.local().minus({ days: 1 }).toFormat('yyyy-LL-dd');
        this.toDate = DateTime.local().minus({ days: 1 }).toFormat('yyyy-LL-dd');
        this.showFromDate = false;
        this.showToDate = false;
        this.selectedNote = null;
        this.selectedStudentName = '';
        this.selectedSubjectName = '';
        // #endregion
        // #region @Watchers
        // #endregion
    }
    get logFilters() {
        const filter = {
            group_id: this.group.id,
        };
        if (this.fromDate && this.toDate) {
            filter.period = [`${this.fromDate} 00:00`, `${this.toDate} 23:59`];
        }
        if (this.tableFilters.action) {
            filter.action = this.tableFilters.action;
        }
        if (this.tableFilters.student) {
            filter.student_ids = this.tableFilters.student;
        }
        return filter;
    }
    // #endregion
    // #region Lifecycle Hooks
    // #endregion
    // #region Event handlers
    // #endregion
    // #region Class methods
    close() {
        this.$emit('close');
    }
    truncateNoteHtmlToText(htmlContent, maxLength) {
        const cleaned = (htmlContent || '')
            .replace(/<\/?(div|p)[^>]*>/gi, ' ')
            .replace(/<br\s*\/?>/gi, ' ')
            .replace(/<li[^>]*>/gi, '• ')
            .replace(/<\/li>/gi, ' ')
            .replace(/<[^>]*>/g, ' ')
            .replace(/&nbsp;/gi, ' ')
            .replace(/\s+/g, ' ')
            .trim();
        if (cleaned.length <= maxLength)
            return cleaned;
        let truncated = cleaned.substring(0, maxLength);
        const cutPoint = truncated.lastIndexOf(' ');
        if (cutPoint > maxLength * 0.7)
            truncated = truncated.substring(0, cutPoint);
        return truncated + '...';
    }
    openSubjectNoteDialog(log) {
        const author = log.user || new User({ name: this.$tc('system') });
        this.selectedStudentName = log.student?.name || '-';
        this.selectedSubjectName = log.meta?.subject_name || '-';
        this.selectedNote = new GoalPlannerNote({
            id: log.id || '',
            note: log.description || '',
            created_at: log.created_at || '',
            start_date: log.created_at || '',
            author,
        });
        this.subjectNoteDialogIsOpen = true;
    }
    closeSubjectNoteDialog() {
        this.subjectNoteDialogIsOpen = false;
    }
    getDotColor(key) {
        switch (key) {
            case LabelKey.HIGHLIGHTED:
                return '#ffc100';
            case LabelKey.BUSY:
                return '#008ac4';
            case LabelKey.CLOSED:
                return '#d0021b';
            case LabelKey.OLD:
                return '#ff7800';
            case LabelKey.INACTIVE:
                return '#9b9b9b';
            case LabelKey.FINISHED:
                return '#00b900';
            case LabelKey.FUTURE:
                return '#000000';
            case LabelKey.LESSON_DONE:
                return '#800080';
            default:
                return '#9b9b9b';
        }
    }
    getStatusLabelFromLog(log) {
        const meta = log?.meta || {};
        return {
            new_label: meta.new_label || undefined,
            old_label: meta.old_label || undefined,
        };
    }
    getStatusColorsFromLog(log) {
        const meta = log?.meta || {};
        return {
            old_status: meta.old_status ? this.getDotColor(meta.old_status) : undefined,
            new_status: meta.new_status ? this.getDotColor(meta.new_status) : undefined,
        };
    }
    sanitize(html) {
        return sanitizeString(html, true);
    }
    stripHtml(html) {
        const d = document.createElement('div');
        d.innerHTML = html || '';
        return (d.textContent || d.innerText || '').trim();
    }
    isLong(html, maxLength = 100) {
        return this.stripHtml(html).length > maxLength;
    }
    // #endregion
    // #region Async methods (api calls only)
    // #endregion
    // #region Getters & Setters
    get tableOptions() {
        return {
            headers: [
                {
                    text: this.$tc('report.date'),
                    value: 'created_at',
                    transform: (created_at) => {
                        if (!created_at)
                            return '';
                        return DateTime.fromSQL(created_at).toFormat('dd-LL-yyyy HH:mm').toString();
                    },
                    sortable: {
                        key: 'created',
                        order: 'ASC',
                    },
                    width: '10%',
                },
                {
                    text: this.$tc('users'),
                    value: 'user',
                    transform: (user) => (user && user.name) || this.$tc('system'),
                    sortable: {
                        key: 'created',
                        order: 'ASC',
                    },
                    width: '10%',
                },
                {
                    text: 'Type',
                    value: 'action',
                    transform: (action) => {
                        if (!action)
                            return '';
                        switch (action) {
                            case 'model:user_subject':
                                return 'Status';
                            case 'model:subject_user_note':
                                return this.$tc('Note');
                            default:
                                return action;
                        }
                    },
                    width: '10%',
                },
                {
                    text: this.$tc('Student name'),
                    value: 'student',
                    transform: (student) => student?.name || '',
                    width: '10%',
                },
                {
                    text: this.$tc('Action'),
                    value: 'meta',
                    slot: 'meta',
                },
            ],
            actions: [
                {
                    type: 'view',
                    label: 'view',
                    icon: 'remove_red_eye',
                    visible: (vm, log) => {
                        return log?.action === 'model:subject_user_note';
                    },
                    action: (vm, log) => {
                        if (log?.action === 'model:subject_user_note') {
                            this.openSubjectNoteDialog(log);
                        }
                    },
                },
            ],
            filters: [],
            model: new Log().filter(this.logFilters).include(['user', 'student', 'meta']).limit(200),
        };
    }
};
__decorate([
    Prop({ required: true }),
    __metadata("design:type", typeof (_a = typeof Group !== "undefined" && Group) === "function" ? _a : Object)
], GoalPlannerLogbookDialog.prototype, "group", void 0);
GoalPlannerLogbookDialog = __decorate([
    Component({
        components: {
            DataTable,
            SingleNoteDialog,
            StudentSelect,
        },
    })
], GoalPlannerLogbookDialog);
export default GoalPlannerLogbookDialog;
// #endregion
// #region Constants
// #endregion
